/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Connected data", description = "Connect the platforms your team uses and configure the data agents use for campaign analysis, creative assets, and personalized messages.", lastUpdatedDate = new Date(1793232000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Connected data",
        description: "Connect the platforms your team uses and configure the data agents use for campaign analysis, creative assets, and personalized messages.",
        lastUpdatedDate: "Thu Oct 29 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Channels",
          "depth": 0,
          "id": "channels"
        }, {
          "value": "Connect advertising and lifecycle platforms",
          "depth": 1,
          "id": "connect-advertising-and-lifecycle-platforms"
        }, {
          "value": "Manage channel connections",
          "depth": 1,
          "id": "manage-channel-connections"
        }, {
          "value": "Email service providers",
          "depth": 0,
          "id": "email-service-providers"
        }, {
          "value": "Digital asset managers",
          "depth": 0,
          "id": "digital-asset-managers"
        }, {
          "value": "Connect Figma Designs",
          "depth": 1,
          "id": "connect-figma-designs"
        }, {
          "value": "Export generated ads to your DAM",
          "depth": 1,
          "id": "export-generated-ads-to-your-dam"
        }, {
          "value": "Workflow managers",
          "depth": 0,
          "id": "workflow-managers"
        }, {
          "value": "External context sources",
          "depth": 0,
          "id": "external-context-sources"
        }, {
          "value": "Personalization data",
          "depth": 0,
          "id": "personalization-data"
        }, {
          "value": "Set a default email provider",
          "depth": 1,
          "id": "set-a-default-email-provider"
        }, {
          "value": "Sync personalization fields and content blocks",
          "depth": 1,
          "id": "sync-personalization-fields-and-content-blocks"
        }, {
          "value": "Import personalization fields from a context file",
          "depth": 1,
          "id": "import-personalization-fields-from-a-context-file"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      ol: "ol",
      h4: "h4",
      code: "code",
      br: "br",
      h5: "h5",
      pre: "pre"
    }, _provideComponents(), props.components), {FeatureFlag, PylonLink} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins, data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["The ", _jsxs(_components.a, {
                    href: "/workspace-management/roles#agents",
                    children: [_jsx(_components.strong, {
                      children: "Configure Agents"
                    }), " grant"]
                  }), "."]
                }), _jsxs("li", {
                  children: ["For lifecycle platforms and some ad platforms, a matching ", _jsx(_components.a, {
                    href: "/destinations/overview",
                    children: "destination"
                  }), "."]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Connect the advertising, lifecycle, and creative platforms your team uses, and configure the data agents use for campaign analysis, creative assets, and personalized messages."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Connected data gives agents access to information from the advertising, lifecycle, and creative platforms your team uses. Go to ", _jsx(_components.strong, {
          children: "Context Hub > Connected data"
        }), " and use its two tabs:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Tab"
            }), _jsx(_components.th, {
              children: "What you do there"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Integrations"
              })
            }), _jsx(_components.td, {
              children: "Connect advertising and lifecycle platforms, email service providers (ESPs), digital asset managers (DAMs), and workflow managers. Start syncing external context sources."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Personalization data"
              })
            }), _jsx(_components.td, {
              children: "Choose a default email provider and manage the fields and content blocks Lifecycle Studio uses in personalized messages."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Connecting a platform here is the first step for two other workflows, which are separate tasks:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Connecting a DAM doesn't bring in assets by itself. After you connect it, create a synced collection in ", _jsx(_components.a, {
            href: "/context-hub/assets#files-from-your-digital-asset-manager",
            children: "Assets"
          }), " to choose what to sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Connecting an ESP doesn't set up personalization. Connecting imports templates. The ", _jsx(_components.strong, {
            children: "Personalization data"
          }), " tab sets the default provider and the fields Lifecycle Studio can use."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Email service providers, workflow managers, and the ", _jsx(_components.strong, {
          children: "Personalization data"
        }), " tab are available in workspaces with Lifecycle Studio."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "channels",
        children: "Channels"
      }), "\n", _jsxs(_components.p, {
        children: ["Connect ad and lifecycle platforms on the ", _jsx(_components.strong, {
          children: "Channels"
        }), " card of the ", _jsx(_components.strong, {
          children: "Integrations"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-connected-channels.png",
          alt: "Channels card on the Integrations tab with a Create connection button"
        })
      }), "\n", _jsx(_components.h3, {
        id: "connect-advertising-and-lifecycle-platforms",
        children: "Connect advertising and lifecycle platforms"
      }), "\n", _jsx(_components.p, {
        children: "Connect a channel when you want agents to analyze the campaigns you run on an ad platform or send from a lifecycle platform. Hightouch copies the data from your account through Fivetran, a third-party service that syncs data from marketing platforms on a schedule. Some platforms send you to Fivetran during setup to sign in or confirm settings, and connection alerts and checks also come from Fivetran."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ad platforms"
          }), " sync campaign structure, creative, and performance data. Agents use it to analyze how your ads perform and which creative works."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lifecycle platforms"
          }), " sync metadata about your campaigns, flows, journeys, segments, and templates. Agents use it to analyze what you've sent and to report on it. Engagement events such as opens and clicks come from your warehouse, not from this connection."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For every platform, plan for the initial sync to take hours, and sometimes days. Large accounts and long history windows take longest. If Fivetran asks for a historical sync window, choose the shortest one that covers your use case."
      }), "\n", _jsxs(_components.p, {
        children: ["Before you connect, check what your platform needs. The tables in ", _jsx(_components.a, {
          href: "#ad-platforms",
          children: "Ad platforms"
        }), " and ", _jsx(_components.a, {
          href: "#lifecycle-platforms",
          children: "Lifecycle platforms"
        }), " list the requirements for each one."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create the destination first, if your platform needs one."
          }), " Lifecycle platforms and some ad platforms reuse the credentials of an existing Hightouch ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "destination"
          }), ", so create it before you connect."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check that a lifecycle destination has read access."
          }), " A destination can sync data successfully without the read access a channel needs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Plan how many connections you need."
          }), " Most ad platforms sync every selected ad account through one connection. TikTok Ads supports a separate connection for each TikTok Ads destination. Lifecycle platforms support a separate connection for each account, business unit, or workspace."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To connect a platform:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Connected data"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Integrations"
          }), " tab, open the ", _jsx(_components.strong, {
            children: "Channels"
          }), " card and select ", _jsx(_components.strong, {
            children: "View connections"
          }), ", then select ", _jsx(_components.strong, {
            children: "Create connection"
          }), ". If you have no connections yet, select ", _jsx(_components.strong, {
            children: "Create connection"
          }), " on the card."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a platform. Platforms are grouped under ", _jsx(_components.strong, {
            children: "Ads"
          }), " and ", _jsx(_components.strong, {
            children: "Lifecycle"
          }), ", and you can search by name."]
        }), "\n", _jsxs(_components.li, {
          children: ["Keep the suggested ", _jsx(_components.strong, {
            children: "Connection name"
          }), " or enter your own."]
        }), "\n", _jsx(_components.li, {
          children: "If the platform needs one, select the matching destination."
        }), "\n", _jsxs(_components.li, {
          children: ["For ", _jsx(_components.strong, {
            children: "Facebook Ads"
          }), " and ", _jsx(_components.strong, {
            children: "Google Ads"
          }), ", select the ad accounts to sync in ", _jsx(_components.strong, {
            children: "Enabled accounts"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For ", _jsx(_components.strong, {
            children: "Klaviyo"
          }), ", select a ", _jsx(_components.strong, {
            children: "Conversion metric (recommended)"
          }), ". Without one, flow reports don't sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create connection"
          }), ". Platforms that need you to sign in or confirm setup redirect you to Fivetran. When you finish there, you return to the connection's status page."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-create-connection-choose-platform.png",
          alt: "Create connection wizard listing ad platforms under Ads, with a platform search box"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If a platform shows ", _jsx(_components.strong, {
          children: "Already connected"
        }), ", selecting it opens that connection."]
      }), "\n", _jsx(_components.p, {
        children: "The connection is ready when its initial sync finishes and an agent can answer a question about a campaign you know."
      }), "\n", _jsx(_components.h4, {
        id: "ad-platforms",
        children: "Ad platforms"
      }), "\n", _jsx(_components.p, {
        children: "For platforms that need a destination, the destination fetches ad creative and the Fivetran connection syncs campaign and performance data. Hightouch doesn't pass the destination's credentials to Fivetran, so you authorize each one separately. Both must have access to every ad account you want to sync."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Platform"
            }), _jsx(_components.th, {
              children: "Destination needed"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/meta",
                children: "Facebook Ads"
              })
            }), _jsx(_components.td, {
              children: "Yes, to fetch ad creative"
            }), _jsxs(_components.td, {
              children: ["Authenticate the destination with a ", _jsx(_components.a, {
                href: "/destinations/meta#authenticate-with-a-system-user-token",
                children: "system user token"
              }), " set to never expire. In Fivetran, select ", _jsx(_components.strong, {
                children: "Grant User Access"
              }), ", not a system user token, so Fivetran manages and refreshes its own credentials. Select ad accounts in ", _jsx(_components.strong, {
                children: "Enabled accounts"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/google",
                children: "Google Ads"
              })
            }), _jsx(_components.td, {
              children: "Yes, to fetch ad creative"
            }), _jsxs(_components.td, {
              children: ["Select ad accounts in ", _jsx(_components.strong, {
                children: "Enabled accounts"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/destinations/bingads",
                children: "Microsoft Ads"
              }), ", ", _jsx(_components.a, {
                href: "/destinations/reddit-ads",
                children: "Reddit Ads"
              }), ", ", _jsx(_components.a, {
                href: "/destinations/tiktok",
                children: "TikTok Ads"
              })]
            }), _jsx(_components.td, {
              children: "Yes, to fetch ad creative"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Snapchat Ads, LinkedIn Ads, Pinterest Ads, Amazon Ads, The Trade Desk, Search Ads 360, Display & Video 360, Yahoo DSP, Moloco, Taboola, AppLovin"
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "You grant access when you authorize in Fivetran."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "lifecycle-platforms",
        children: "Lifecycle platforms"
      }), "\n", _jsx(_components.p, {
        children: "Every lifecycle platform uses a destination's credentials. When you connect, Hightouch checks that those credentials have the access Fivetran needs and tells you what's missing. If the destination's key lacks any of this access, update the key or create a new one."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Platform"
            }), _jsx(_components.th, {
              children: "Additional access the connection needs"
            }), _jsx(_components.th, {
              children: "Setup in the platform"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/braze",
                children: "Braze"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "campaigns"
              }), ", ", _jsx(_components.code, {
                children: "canvas"
              }), ", and ", _jsx(_components.code, {
                children: "segments"
              }), " permissions, each with ", _jsx(_components.code, {
                children: ".list"
              }), ", ", _jsx(_components.code, {
                children: ".details"
              }), ", and ", _jsx(_components.code, {
                children: ".data_series"
              })]
            }), _jsx(_components.td, {
              children: "Braze keys can't be edited, so if the destination's key lacks these, create a new key with its existing permissions plus these, update the destination to use it, then reconnect any existing connection."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/sfmc",
                children: "Salesforce Marketing Cloud"
              })
            }), _jsxs(_components.td, {
              children: ["On the installed package Hightouch uses (", _jsx(_components.strong, {
                children: "Setup > Apps > Installed Packages"
              }), "): Read on Campaign, Documents and Images, Email, Journeys, List and Subscribers, Push, and Saved Content. Read and Write on Data Extensions. Read, Write, and Execute on Automations."]
            }), _jsxs(_components.td, {
              children: ["If the destination uses an SFTP username and password, Hightouch passes them to Fivetran, and that user needs folder permissions. In ", _jsx(_components.strong, {
                children: "Setup > Data Management > FTP Accounts"
              }), ", open the user's menu and select ", _jsx(_components.strong, {
                children: "Manage Permissions"
              }), ". Grant the user ", _jsx(_components.strong, {
                children: "Full"
              }), " permissions. Fivetran also needs a folder named exactly ", _jsx(_components.code, {
                children: "Export"
              }), " directly in the user's home folder."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/klaviyo",
                children: "Klaviyo"
              })
            }), _jsxs(_components.td, {
              children: ["A Full Access private key, or ", _jsx(_components.code, {
                children: "campaigns:read"
              }), ", ", _jsx(_components.code, {
                children: "flows:read"
              }), ", ", _jsx(_components.code, {
                children: "forms:read"
              }), ", ", _jsx(_components.code, {
                children: "lists:read"
              }), ", ", _jsx(_components.code, {
                children: "segments:read"
              }), ", ", _jsx(_components.code, {
                children: "metrics:read"
              }), ", ", _jsx(_components.code, {
                children: "templates:read"
              }), ", ", _jsx(_components.code, {
                children: "tags:read"
              }), ", ", _jsx(_components.code, {
                children: "catalogs:read"
              }), ", and ", _jsx(_components.code, {
                children: "events:read"
              }), ". For OAuth, the authorizing user must be an Owner, Admin, or Manager."]
            }), _jsxs(_components.td, {
              children: ["Pick a ", _jsx(_components.strong, {
                children: "Conversion metric (recommended)"
              }), " when you connect, or flow reports won't sync. Confirm setup in Fivetran after you select ", _jsx(_components.strong, {
                children: "Create connection"
              }), ". If the destination uses OAuth, you sign in to Klaviyo in Fivetran, and that's where you reauthorize."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/iterable",
                children: "Iterable"
              })
            }), _jsx(_components.td, {
              children: "A server-side API key. Read-only, mobile, web, and JavaScript keys don't work."
            }), _jsxs(_components.td, {
              children: ["In Fivetran, set ", _jsx(_components.strong, {
                children: "Webhook Authentication"
              }), " to ", _jsx(_components.strong, {
                children: "No Auth"
              }), " and copy the webhook URL it shows. In Iterable, go to ", _jsx(_components.strong, {
                children: "Integrations > System Webhooks"
              }), " and create a webhook. Enter the Fivetran webhook URL as the ", _jsx(_components.strong, {
                children: "Endpoint URL"
              }), " and set authentication to ", _jsx(_components.strong, {
                children: "None"
              }), ". Save the webhook, then edit it to turn it on and, under ", _jsx(_components.strong, {
                children: "Email"
              }), ", select at least ", _jsx(_components.strong, {
                children: "Blast send"
              }), " and ", _jsx(_components.strong, {
                children: "Triggered send"
              }), ". Then finish setup in Fivetran."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/marketo",
                children: "Marketo"
              })
            }), _jsx(_components.td, {
              children: "Read-Only Assets and Read-Only Campaign on the API user's role"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/attentive",
                children: "Attentive"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "product_catalogs:write"
              })
            }), _jsxs(_components.td, {
              children: ["In your Attentive app settings, set ", _jsx(_components.strong, {
                children: "Event Webhook Status"
              }), " to on and select the subscription webhook."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/sailthru",
                children: "Sailthru"
              })
            }), _jsx(_components.td, {
              children: "No additional access needed"
            }), _jsxs(_components.td, {
              children: ["If your account restricts API access by IP, allow ", _jsx(_components.a, {
                href: "https://fivetran.com/docs/security-and-privacy/ips",
                children: "Fivetran's IP addresses"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "manage-channel-connections",
        children: "Manage channel connections"
      }), "\n", _jsxs(_components.p, {
        children: ["To see which connections need attention, go to ", _jsx(_components.strong, {
          children: "Context Hub > Connected data"
        }), " and select ", _jsx(_components.strong, {
          children: "View connections"
        }), " on the ", _jsx(_components.strong, {
          children: "Channels"
        }), " card. Each connection shows its status and last run, and connections that need attention are listed first."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select a connection to open its status page, which shows its schedule and when it last succeeded or failed. ", _jsx(_components.strong, {
          children: "Alerts"
        }), " lists open issues from Fivetran and any setup checks that failed, with what to fix. ", _jsx(_components.strong, {
          children: "Runs"
        }), " lists each sync run, its retries, and the rows it synced per table."]
      }), "\n", _jsx(_components.p, {
        children: "From the status page you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Change the connection's settings"
          }), " — Select ", _jsx(_components.strong, {
            children: "Edit"
          }), " to change the name, destination, enabled accounts, or Klaviyo conversion metric. Changing the destination or conversion metric reauthorizes the connection. To reauthorize without other changes, select ", _jsx(_components.strong, {
            children: "Reconnect"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check the setup"
          }), " — Select ", _jsx(_components.strong, {
            children: "Test connection"
          }), " to run Fivetran's setup checks and see each result. It's unavailable while a sync is running."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync right away"
          }), " — Select ", _jsx(_components.strong, {
            children: "Sync now"
          }), " to start a sync. It's unavailable while a sync is running or the connection is paused."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Stop syncing for a while"
          }), " — Select ", _jsx(_components.strong, {
            children: "Pause"
          }), ", and select ", _jsx(_components.strong, {
            children: "Resume"
          }), " when you're ready to sync again."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Remove the connection"
          }), " — Select ", _jsx(_components.strong, {
            children: "Delete"
          }), " from the actions menu. Deleting removes the connection and all of its synced data, and it can't be undone."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "If a connection breaks"
          }), _jsx(_components.br, {}), "\n", "Open it from ", _jsx(_components.strong, {
            children: "View connections"
          }), " and check ", _jsx(_components.strong, {
            children: "Alerts"
          }), ". To rerun Fivetran's checks with the credentials Fivetran already has, select ", _jsx(_components.strong, {
            children: "Test connection"
          }), ". To send the destination's current credentials to Fivetran, select ", _jsx(_components.strong, {
            children: "Reconnect"
          }), " under ", _jsx(_components.strong, {
            children: "Edit"
          }), ". For platforms you sign in to through Fivetran, ", _jsx(_components.strong, {
            children: "Reconnect"
          }), " also redirects you to sign in again."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "email-service-providers",
        children: "Email service providers"
      }), "\n", _jsxs(_components.p, {
        children: ["Connect an email service provider (ESP) to import your existing email templates into Hightouch for use in Lifecycle Studio. You need an ESP ", _jsx(_components.a, {
          href: "/destinations/overview",
          children: "destination"
        }), " for the provider before you can import from it."]
      }), "\n", _jsx(_components.p, {
        children: "An ESP connection only imports email templates. To let agents analyze the campaigns you send from the same tool, connect it as a channel too. Braze, Klaviyo, Iterable, Salesforce Marketing Cloud, and Sailthru can appear in both places."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-connected-email-service-providers.png",
          alt: "Email Service Providers card with Salesforce Marketing Cloud selected and a View Templates button"
        })
      }), "\n", _jsx(_components.p, {
        children: "To import templates:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Connected data > Integrations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Email Service Providers"
          }), ", select your provider from the dropdown."]
        }), "\n", _jsxs(_components.li, {
          children: ["If no destination exists for that provider, select ", _jsx(_components.strong, {
            children: "Connect your ESP"
          }), " to create one."]
        }), "\n", _jsxs(_components.li, {
          children: ["After a destination exists, it appears by its ", _jsx(_components.strong, {
            children: "Integration name"
          }), ". Select ", _jsx(_components.strong, {
            children: "View Templates"
          }), " for that destination."]
        }), "\n", _jsx(_components.li, {
          children: "Search for templates by name. For Iterable, you can also filter by template type."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the templates to import, optionally assign them to one or more ", _jsx(_components.strong, {
            children: "Brand profiles"
          }), ", and select ", _jsx(_components.strong, {
            children: "Import"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The preview shows each template's name, subject line, preheader, tags, and HTML."
      }), "\n", _jsx(_components.p, {
        children: "Xtremepush also appears in the provider list. Lifecycle Studio uses an Xtremepush destination to export generated emails to your Xtremepush account rather than to import templates."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "digital-asset-managers",
        children: "Digital asset managers"
      }), "\n", _jsxs(_components.p, {
        children: ["Connect a digital asset manager (DAM), the tool where your team stores brand assets, so agents can use your images and creative files. Connecting a DAM authorizes Hightouch to read from it. You choose which assets to bring in when you add a synced collection in ", _jsx(_components.strong, {
          children: "Context Hub > Assets"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Digital Asset Managers"
        }), " card lists the providers available in your workspace, including Orange Logic, Canto, Adobe Experience Manager Assets, Aprimo, Bynder, Shade, Frontify, and Iconik. Each provider card shows ", _jsx(_components.strong, {
          children: "Not configured"
        }), " until you connect it, then ", _jsx(_components.strong, {
          children: "Connected"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-connected-dam.png",
          alt: "Digital Asset Managers card with DAM providers and the Figma plugin card"
        })
      }), "\n", _jsx(_components.p, {
        children: "To connect a DAM:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Connected data > Integrations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Digital Asset Managers"
          }), ", select the provider's card."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab, enter a ", _jsx(_components.strong, {
            children: "Name"
          }), " and the credentials the provider asks for."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Test connection"
          }), " to confirm Hightouch can reach your DAM, then select ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Assets"
          }), " and ", _jsx(_components.a, {
            href: "/context-hub/assets#files-from-your-digital-asset-manager",
            children: "add a synced collection"
          }), " to choose which assets to bring in."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Two Figma cards appear in this section, and they do different jobs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Figma Designs"
          }), " — A connection that lets Hightouch read your Figma files, so you can import frames as templates. Set it up with a personal access token, as described in ", _jsx(_components.a, {
            href: "#connect-figma-designs",
            children: "Connect Figma Designs"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Figma"
          }), " — Shows ", _jsx(_components.strong, {
            children: "Plugin available"
          }), " and opens the Hightouch plugin in the Figma Community. The plugin runs inside Figma and lets designers bring Hightouch ads into Figma. It isn't a connection in Hightouch. See ", _jsx(_components.a, {
            href: "/ad-studio/figma",
            children: "Hand off to Figma"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Neither card supports synced collections."
      }), "\n", _jsx(_components.h3, {
        id: "connect-figma-designs",
        children: "Connect Figma Designs"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Figma Designs"
      }), "\n", _jsx(_components.p, {
        children: "Connect Figma Designs so you can import Figma frames as templates. In Lifecycle Studio, an imported frame becomes an editable email draft. In Ad Studio, it becomes an ad template."
      }), "\n", _jsx(_components.p, {
        children: "The connection uses a Figma personal access token, so Hightouch can open the same files as the Figma user who created it. Use a token from someone who can open every file your team imports from."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch only reads from Figma. It never edits your files or comments on them."
      }), "\n", _jsx(_components.h4, {
        id: "create-a-token-in-figma",
        children: "Create a token in Figma"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In Figma, open your account menu and select ", _jsx(_components.strong, {
              children: "Settings"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select the ", _jsx(_components.strong, {
              children: "Security"
            }), " tab. Under ", _jsx(_components.strong, {
              children: "Personal access tokens"
            }), ", select ", _jsx(_components.strong, {
              children: "Generate new token"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "context-hub/ch-figma-pat-generate.png",
              alt: "Figma Security settings with the Personal access tokens section and Generate new token link"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter a ", _jsx(_components.strong, {
              children: "Token name"
            }), " that tells you where it's used, such as ", _jsx(_components.code, {
              children: "Hightouch"
            }), ", and choose an ", _jsx(_components.strong, {
              children: "Expiration"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Under ", _jsx(_components.strong, {
              children: "Scopes"
            }), ", select these two scopes and leave every other scope unselected:"]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.code, {
                children: "current_user:read"
              }), " — Lets ", _jsx(_components.strong, {
                children: "Test connection"
              }), " confirm which Figma account the token belongs to."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.code, {
                children: "file_content:read"
              }), " — Lets Hightouch read the frames you import and render their images."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Generate token"
            }), " and copy the token. Figma shows it only once."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "context-hub/ch-figma-pat-copy.png",
              alt: "New Figma personal access token with a prompt to copy it now"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch never writes to Figma, so it doesn't need any ", _jsx(_components.code, {
          children: "write"
        }), " scope. It doesn't read comments, so it doesn't need ", _jsx(_components.code, {
          children: "file_comments:read"
        }), " either. A token with extra scopes still works, but it gives anyone holding it more access than Hightouch uses."]
      }), "\n", _jsx(_components.h4, {
        id: "add-the-token-in-hightouch",
        children: "Add the token in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to ", _jsx(_components.strong, {
              children: "Context Hub > Connected data > Integrations"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Digital Asset Managers"
            }), ", select ", _jsx(_components.strong, {
              children: "Figma Designs"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Paste the token in ", _jsx(_components.strong, {
              children: "Personal access token"
            }), " and select ", _jsx(_components.strong, {
              children: "Save token"
            }), ". Tokens start with ", _jsx(_components.code, {
              children: "figd_"
            }), "."]
          }), "\n", _jsxs(_components.p, {
            children: ["After you save, the ", _jsx(_components.strong, {
              children: "Figma Designs"
            }), " card shows ", _jsx(_components.strong, {
              children: "Connected"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "context-hub/ch-figma-designs-card.png",
              alt: "Figma Designs card showing Connected, with its configuration panel open"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["To confirm the token works, open ", _jsx(_components.strong, {
              children: "Figma Designs"
            }), " again and select ", _jsx(_components.strong, {
              children: "Test connection"
            }), ". A successful test shows the Figma account the token belongs to."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "context-hub/ch-figma-designs-test-success.png",
              alt: "Figma Designs configuration with a Connection successful message"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To import a frame, go to ", _jsx(_components.a, {
          href: "/lifecycle-studio/templates#import-from-figma",
          children: "Import from Figma"
        }), " in Lifecycle Studio."]
      }), "\n", _jsx(_components.h4, {
        id: "replace-an-expired-token",
        children: "Replace an expired token"
      }), "\n", _jsx(_components.p, {
        children: "Figma tokens stop working on the expiration date you chose. Imports also fail if someone revokes the token in Figma. Before the token expires, create a new one and swap it in:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Create a new token in Figma with the same two scopes."
        }), "\n", _jsxs(_components.li, {
          children: ["Open ", _jsx(_components.strong, {
            children: "Figma Designs"
          }), " and select ", _jsx(_components.strong, {
            children: "Edit"
          }), " next to ", _jsx(_components.strong, {
            children: "Personal access token"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Paste the new token, select ", _jsx(_components.strong, {
            children: "Test connection"
          }), ", and then select ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "troubleshoot-figma-designs",
        children: "Troubleshoot Figma Designs"
      }), "\n", _jsx(_components.h5, {
        id: "figma-access-token-is-invalid-or-revoked",
        children: "Figma access token is invalid or revoked"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Test connection"
        }), " shows this message when Figma rejects the token:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Figma access token is invalid or revoked. Reconnect to continue.\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The token has expired, was revoked, or was pasted incompletely. ", _jsx(_components.a, {
          href: "#replace-an-expired-token",
          children: "Replace the token"
        }), "."]
      }), "\n", _jsx(_components.h5, {
        id: "figma-rejected-the-access-token",
        children: "Figma rejected the access token"
      }), "\n", _jsxs(_components.p, {
        children: ["An import fails with a message that starts with ", _jsx(_components.code, {
          children: "Figma rejected the access token"
        }), " and ends with ", _jsx(_components.code, {
          children: "Reconnect Figma to continue."
        })]
      }), "\n", _jsx(_components.p, {
        children: "Check these in order:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The token expired or was revoked."
          }), " ", _jsx(_components.a, {
            href: "#replace-an-expired-token",
            children: "Replace the token"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The token is missing a scope."
          }), " Create a token with ", _jsx(_components.code, {
            children: "current_user:read"
          }), " and ", _jsx(_components.code, {
            children: "file_content:read"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The token's owner can't open the file."
          }), " Share the file with that Figma user, or use a token from someone who already has access."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "export-generated-ads-to-your-dam",
        children: "Export generated ads to your DAM"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "DAM export"
      }), "\n", _jsx(_components.p, {
        children: "Export ads you create in Ad Studio to your DAM so they sit next to the rest of your brand assets. Bynder, Aprimo, and Frontify connections support export. Each ad uploads as a PNG file."
      }), "\n", _jsx(_components.p, {
        children: "Set up export once per connection:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Connected data > Integrations"
          }), " and select your connected DAM."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Export"
          }), " tab, enter where exported files go. Bynder asks for a ", _jsx(_components.strong, {
            children: "Bynder brand ID"
          }), ", and Frontify asks for a ", _jsx(_components.strong, {
            children: "Frontify library ID"
          }), " and an optional ", _jsx(_components.strong, {
            children: "Folder ID"
          }), ". Aprimo needs only metadata."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optionally, add metadata. ", _jsx(_components.strong, {
            children: "Static metadata fields"
          }), " apply the same value to every exported file. ", _jsx(_components.strong, {
            children: "Dynamic metadata fields"
          }), " fill a DAM field from the ad, such as its project name or a link back to the ad in Hightouch, or ask the person exporting to choose a value."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save export settings"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After you save export settings, ", _jsx(_components.strong, {
          children: "Export to DAM"
        }), " appears on ads in Ad Studio, for a single ad or a selection of ads. In the ", _jsx(_components.strong, {
          children: "Export to DAM"
        }), " dialog, choose any ", _jsx(_components.strong, {
          children: "Additional sizes"
        }), " to include, fill in any values your export settings ask for, and start the export. Each ad's original size is always exported, and one export can include up to 100 files."]
      }), "\n", _jsxs(_components.p, {
        children: ["To check on an export, open the connection's ", _jsx(_components.strong, {
          children: "Export history"
        }), " tab. It shows each export's status, the files that exported or failed, and links to the exported files in your DAM. Select ", _jsx(_components.strong, {
          children: "Retry"
        }), " to rerun the files that failed or didn't finish."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "workflow-managers",
        children: "Workflow managers"
      }), "\n", _jsx(_components.p, {
        children: "Connect a workflow manager so your team can manage Lifecycle Studio campaigns from where they plan the work, and update tasks there from Hightouch. Hightouch supports Asana."
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Asana"
        }), " card shows where setup stands:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Not connected"
          }), " — Asana isn't connected to Hightouch yet."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "App not added"
          }), " — Hightouch is connected, but the Hightouch app isn't added in Asana yet."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connected"
          }), " — Asana is connected and the app is added."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-connected-workflow-managers.png",
          alt: "Workflow managers card with the Asana card showing Not connected"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "Asana"
        }), " card and follow the setup steps. For authentication options, installing the app, and what you can do from each side, see the ", _jsx(_components.a, {
          href: "/lifecycle-studio/asana",
          children: "Asana integration"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To ask about other tools, ", _jsx(PylonLink, {
          children: "contact us"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "external-context-sources",
        children: "External context sources"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync governed definitions from your warehouse, such as Snowflake semantic views or Databricks metric views, so agents use the metric definitions your data team maintains. Synced files appear in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Files"
        }), " with a ", _jsx(_components.strong, {
          children: "Synced"
        }), " badge, and they refresh on a schedule."]
      }), "\n", _jsxs(_components.p, {
        children: ["Add a source from the ", _jsx(_components.strong, {
          children: "External context sources"
        }), " card on the ", _jsx(_components.strong, {
          children: "Integrations"
        }), " tab by selecting ", _jsx(_components.strong, {
          children: "Add external context"
        }), ". For warehouse requirements and setup steps, see ", _jsx(_components.a, {
          href: "/context-hub/external",
          children: "Sync external context"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "personalization-data",
        children: "Personalization data"
      }), "\n", _jsxs(_components.p, {
        children: ["Use the ", _jsx(_components.strong, {
          children: "Personalization data"
        }), " tab to control how Lifecycle Studio writes emails for your email service provider: which provider it writes for by default, and which personalization fields and content blocks it can use there."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-personalization-data.png",
          alt: "Personalization data tab with the Workspace default picker and a connected Salesforce Marketing Cloud account"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-a-default-email-provider",
        children: "Set a default email provider"
      }), "\n", _jsxs(_components.p, {
        children: ["Lifecycle Studio sends email through either an ESP destination or Native Delivery, Hightouch's own sending service. It writes each email in that destination's templating syntax, so set ", _jsx(_components.strong, {
          children: "Workspace default"
        }), " under ", _jsx(_components.strong, {
          children: "Default email service provider"
        }), " to the destination most of your email goes through."]
      }), "\n", _jsxs(_components.p, {
        children: ["A brand can override the workspace default with its own ", _jsx(_components.strong, {
          children: "Email provider"
        }), " setting on its ", _jsx(_components.a, {
          href: "/context-hub/brands",
          children: "brand profile"
        }), ", and a campaign can override both. Lifecycle Studio uses the campaign's setting first, then the brand's, then the workspace default."]
      }), "\n", _jsxs(_components.p, {
        children: ["When the workspace default is an ESP, ", _jsx(_components.strong, {
          children: "Conditional type"
        }), " sets how Lifecycle Studio writes new dynamic-content conditions. Keep ", _jsx(_components.strong, {
          children: "ESP"
        }), ", the default, to base conditions on the provider's own profile rules. Select ", _jsx(_components.strong, {
          children: "Hightouch"
        }), " to base them on your warehouse audience definitions. To build dynamic content, see ", _jsx(_components.a, {
          href: "/lifecycle-studio/dynamic-content",
          children: "Dynamic content"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-personalization-fields-and-content-blocks",
        children: "Sync personalization fields and content blocks"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch reads the personalization fields and content blocks saved in your ESP, so generated emails reference attributes that exist in your provider. ", _jsx(_components.strong, {
          children: "Connected Providers"
        }), " lists each ESP destination whose catalog Hightouch can read: Braze, Iterable, Klaviyo, Salesforce Marketing Cloud, and Xtremepush. Each account shows when it last synced, any brands that send through it, and a ", _jsx(_components.strong, {
          children: "Default"
        }), " badge if it's the workspace default. Select ", _jsx(_components.strong, {
          children: "Add another destination"
        }), " to connect another ESP."]
      }), "\n", _jsx(_components.p, {
        children: "Select an account to see its catalog:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Fields"
          }), " — Personalization fields and the merge tag for each."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Content blocks"
          }), " — Reusable blocks saved in your provider, with the reference tag for each. Xtremepush syncs fields only."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When the fields or content blocks in your provider change, select ", _jsx(_components.strong, {
          children: "Resync"
        }), " to read them again and replace what Hightouch has cached. Large accounts can take a few minutes, and the resync counts against your provider's API rate limit."]
      }), "\n", _jsx(_components.p, {
        children: "You can delete a field or content block from the Hightouch catalog without changing your provider. If it still exists in your provider, the next resync brings it back."
      }), "\n", _jsx(_components.h3, {
        id: "import-personalization-fields-from-a-context-file",
        children: "Import personalization fields from a context file"
      }), "\n", _jsxs(_components.p, {
        children: ["If your provider's catalog is missing profile attributes you personalize with, list them in a Markdown ", _jsx(_components.a, {
          href: "/context-hub/business-context#add-business-context",
          children: "context file"
        }), " in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Files"
        }), ", then import them:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Fields"
          }), " tab, select ", _jsx(_components.strong, {
            children: "Import from context"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Context file"
          }), " that lists the attributes, and select ", _jsx(_components.strong, {
            children: "Extract fields"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Review the extracted fields under ", _jsx(_components.strong, {
            children: "Review profile fields"
          }), ". Hightouch builds each merge tag for your provider from the field name."]
        }), "\n", _jsx(_components.li, {
          children: "Clear any fields you don't want, then import the rest."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-import-from-context-extract-fields.png",
          alt: "Import from context dialog with a context file selected and the Extract fields button"
        })
      }), "\n", _jsx(_components.p, {
        children: "New fields are added to the catalog, and fields with existing names are left unchanged. A later resync from your provider can still replace the full set."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/assets#files-from-your-digital-asset-manager",
            children: "Create a synced asset collection"
          }), " to choose which files from your connected DAM agents can use."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/external",
            children: "Sync external context"
          }), " to grant warehouse access and select the Snowflake or Databricks views to sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/brands#set-the-brands-email-provider",
            children: "Define your brand profiles"
          }), " to set a different email provider for a brand that doesn't use the workspace default."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
